import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NextUp } from "@/components/Callouts";

/* Inline diagram for the Melakarta permutation count. */
function MelakartaCount() {
  return (
    <div className="my-5 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] to-purple-500/[0.05] p-5">
      <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300">
        Why 72 parent scales?
      </div>
      <div className="grid gap-3 text-[13px] sm:grid-cols-3">
        <div className="rounded-lg border border-nsm-border bg-zinc-900/50 p-3">
          <div className="font-mt text-base font-semibold text-amber-200">3 Re's × 3 Ga's</div>
          <div className="mt-1 text-nsm-dim">
            But Re2 = Ga1 in pitch — so only <strong>6 valid Re-Ga pairs</strong>, not nine.
          </div>
        </div>
        <div className="rounded-lg border border-nsm-border bg-zinc-900/50 p-3">
          <div className="font-mt text-base font-semibold text-amber-200">3 Dha's × 3 Ni's</div>
          <div className="mt-1 text-nsm-dim">
            Same overlap — Dha2 = Ni1, Dha3 = Ni2 — so <strong>6 valid Dha-Ni pairs</strong>.
          </div>
        </div>
        <div className="rounded-lg border border-nsm-border bg-zinc-900/50 p-3">
          <div className="font-mt text-base font-semibold text-amber-200">2 Ma's</div>
          <div className="mt-1 text-nsm-dim">
            Either <strong>Ma1</strong> (perfect 4) or <strong>Ma2</strong> (sharp 4 / tritone).
          </div>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-center font-mt text-lg text-amber-100">
        6 × 6 × 2 = <strong className="text-2xl">72</strong> Melakarta ragas
      </div>
    </div>
  );
}

/* Inline diagram for the swara options. */
function SwaraTable() {
  const rows: Array<{ deg: string; name: string; options: { label: string; western: string }[] }> = [
    { deg: "1",  name: "Sa",  options: [{ label: "Sa",  western: "C — root" }] },
    { deg: "2",  name: "Re",  options: [
      { label: "R1 · Shuddha Ri",      western: "D♭ — minor 2nd" },
      { label: "R2 · Chatushruti Ri",  western: "D — major 2nd" },
      { label: "R3 · Shatshruti Ri",   western: "D♯ — augmented 2nd" },
    ]},
    { deg: "3",  name: "Ga",  options: [
      { label: "G1 · Shuddha Ga",   western: "E♭♭ — diminished 3rd (= D)" },
      { label: "G2 · Sadharana Ga", western: "E♭ — minor 3rd" },
      { label: "G3 · Antara Ga",    western: "E — major 3rd" },
    ]},
    { deg: "4",  name: "Ma",  options: [
      { label: "M1 · Shuddha Ma", western: "F — perfect 4th" },
      { label: "M2 · Prati Ma",   western: "F♯ — augmented 4th / tritone" },
    ]},
    { deg: "5",  name: "Pa",  options: [{ label: "Pa", western: "G — perfect 5th (always present)" }] },
    { deg: "6",  name: "Dha", options: [
      { label: "D1 · Shuddha Dha",     western: "A♭ — minor 6th" },
      { label: "D2 · Chatushruti Dha", western: "A — major 6th" },
      { label: "D3 · Shatshruti Dha",  western: "A♯ — augmented 6th" },
    ]},
    { deg: "7",  name: "Ni",  options: [
      { label: "N1 · Shuddha Ni", western: "B♭♭ — diminished 7th (= A)" },
      { label: "N2 · Kaisiki Ni", western: "B♭ — minor 7th" },
      { label: "N3 · Kakali Ni",  western: "B — major 7th" },
    ]},
  ];
  return (
    <div className="my-5 overflow-hidden rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/[0.05] to-amber-500/[0.05]">
      <div className="border-b border-nsm-border bg-zinc-900/40 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-purple-300">
        The seven swaras and their variants (with C as Sa)
      </div>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-nsm-border text-left text-[11px] uppercase tracking-wider text-nsm-dim">
            <th className="px-4 py-2">Degree</th>
            <th className="px-4 py-2">Swara</th>
            <th className="px-4 py-2">Variants</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.deg} className="border-b border-nsm-border/50 align-top">
              <td className="px-4 py-2 font-mt text-base text-amber-300">{r.deg}</td>
              <td className="px-4 py-2 font-mt text-base text-nsm-ink">{r.name}</td>
              <td className="px-4 py-2 text-nsm-dim">
                <ul className="space-y-1">
                  {r.options.map((o) => (
                    <li key={o.label}>
                      <strong className="text-amber-200">{o.label}</strong>
                      <span className="mx-2 text-zinc-600">·</span>
                      <span>{o.western}</span>
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MusicTheory16() {
  const { lesson, course, accent } = findLesson("music-theory", "16");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT16-Melakarta-Animated.mp4"
        caption="Melakarta — 72 parent ragas in 12 chakras (Shuddha / Prati Ma)"
        hint="≈ 26 sec · watch first"
      />
      <Chapter number="16" eyebrow="Music Theory · Chapter Sixteen" title="Carnatic scale theory, for everyone">
        <Prose>
          <p>
            Western music theory has named perhaps a dozen seven-note scales — major,
            natural minor, harmonic minor, melodic minor, and the four common modes
            (Dorian, Phrygian, Lydian, Mixolydian). South Indian classical music takes
            the same problem — <em>how do you choose seven notes from the twelve?</em> —
            and answers it with stunning systematic completeness. The answer is a system
            called <strong>Melakarta</strong>, a family of seventy-two parent scales
            from which the other Carnatic ragas — the <em>janya</em> ragas — are derived.
          </p>
          <p>
            This chapter is not a deep dive into Carnatic music — it's an
            invitation. We'll use the Melakarta framework <em>intervalically</em>, in a
            way that maps cleanly onto Western intervals, so a Western musician can read
            it and an Indian musician can revise it. The pay-off is huge: once you
            understand the seventy-two-scale generator, you stop memorising scales and
            start <em>building</em> them. Any seven-note scale you ever want becomes
            reachable through the same recipe.
          </p>
        </Prose>
        <MTQuote>
          I'm going to definitely tell you how you can build every scale. This is a concept
          which will allow you to build pretty much every single scale, or at least the
          seven-note scales which are famous across the world.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The seven swaras" title="Sa, Re, Ga, Ma, Pa, Dha, Ni — and their variants">
        <Prose>
          <p>
            Indian music names its seven scale degrees using syllables: <strong>Sa, Re,
            Ga, Ma, Pa, Dha, Ni</strong>. They correspond directly to the seven Western
            scale degrees 1, 2, 3, 4, 5, 6, 7. But where Western theory thinks of the
            second degree as either a major 2nd or a minor 2nd, Indian theory recognises
            <em> three</em> possible Re's. Where Western theory has only two fourths,
            Indian theory has two Ma's. The choices are richer.
          </p>
          <p>
            Two notes are <strong>fixed</strong> in every Melakarta raga: <strong>Sa</strong>
            (the root) and <strong>Pa</strong> (the perfect fifth). They never move; they
            are the gravitational anchors of the scale. The remaining five notes — Re,
            Ga, Ma, Dha, Ni — each have multiple variants you can choose from.
          </p>
        </Prose>

        <SwaraTable />

        <Prose>
          <p>
            A word on the names, because this is where the two Indian traditions part
            company. Carnatic music gives every variant its own name — <strong>Shuddha</strong>
            ("pure") is always the LOWEST version of a degree, and the ones above it get names
            of their own: Chatushruti and Shatshruti for Ri and Dha, Sadharana and Antara for
            Ga, Kaisiki and Kakali for Ni, and Prati for the raised Ma. So Shuddha Ri is the
            <em>minor</em> 2nd, not the major one. Hindustani music uses a different, smaller
            vocabulary — <em>komal</em> for a lowered note and <em>tivra</em> for the one raised
            note it needs — so you will meet those words too, but they are not the Carnatic
            terms and don't map onto this table. In Carnatic numbering the rule is simply
            R1 &lt; R2 &lt; R3 by pitch.
          </p>
          <p>
            The Western names in the right column matter for one practical reason:
            <strong> notation</strong>. When you write a scale on the staff using a
            chosen Re and Ga, you must use letter-name discipline — every letter from A
            to G must appear exactly once. That's why the diminished 3rd ends up
            spelled <strong>E♭♭</strong> rather than D, even though D and E♭♭ are the
            same key on the piano. The letter has to be E because we already used D for
            the Re — and the seven-letter rule is the same one we obeyed in Chapter 1
            for major scales.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The pitch overlap" title="Why 72, not 162, ragas">
        <Prose>
          <p>
            If you do the naïve combinatorics — three Re's, three Ga's, two Ma's, three
            Dha's, three Ni's — you get 3 × 3 × 2 × 3 × 3 = 162 scales. The actual
            number of Melakarta ragas is exactly 72. Where do the other 90 go?
          </p>
          <p>
            They are eliminated by a beautiful constraint: <strong>two swaras cannot
            occupy the same pitch in the same scale</strong>. R2 and G1 are different
            <em> names</em> (one is a "Re", the other is a "Ga") but the same
            <em> pitch</em> on the piano — both fall on D. So any scale that tries to
            include both R2 and G1 would have two of the same note, which violates the
            seven-letter rule and the no-pitch-collision rule.
          </p>
          <p>
            The same overlap happens between Dha and Ni: D2 and N1 share a pitch, as do
            D3 and N2. So instead of nine valid Re-Ga combinations, only six work; and
            instead of nine valid Dha-Ni combinations, only six work. That's why the
            real arithmetic is:
          </p>
        </Prose>

        <MelakartaCount />

        <TheoryBox label="The Melakarta rule">
          A Melakarta raga uses every degree exactly once: Sa, Re, Ga, Ma, Pa, Dha, Ni.
          Sa is fixed at the root. Pa is fixed at the perfect 5th. The other five
          degrees each pick from their available variants — but no two degrees can land
          on the same pitch. Six valid Re-Ga pairs × six valid Dha-Ni pairs × two Ma's
          gives exactly 72 parent scales.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="A worked example" title="Spelling the major scale in Carnatic clothes">
        <Prose>
          <p>
            Take the most familiar scale of all — the C major scale — and translate it
            into Melakarta notation. C major is C, D, E, F, G, A, B. In Carnatic terms,
            that's:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>Sa</strong> = C (root, fixed)</li>
            <li><strong>R2</strong> = D (Shuddha Re, major 2nd)</li>
            <li><strong>G3</strong> = E (Shuddha Ga, major 3rd)</li>
            <li><strong>M1</strong> = F (Shuddha Ma, perfect 4th)</li>
            <li><strong>Pa</strong> = G (perfect 5th, fixed)</li>
            <li><strong>D2</strong> = A (Shuddha Dha, major 6th)</li>
            <li><strong>N3</strong> = B (Shuddha Ni, major 7th)</li>
          </ul>
          <p>
            So the major scale in Melakarta notation is
            <strong> Sa R2 G3 M1 Pa D2 N3</strong>. In Carnatic music it has its own
            name — <em>Shankarabharanam</em> — and it sits as one specific entry among
            the 72 parent scales.
          </p>
          <p>
            The natural minor scale is another example. C natural minor (C, D, E♭, F, G,
            A♭, B♭) translates as <strong>Sa R2 G2 M1 Pa D1 N2</strong> — and that scale
            is called <em>Natabhairavi</em>. Same Sa, same Pa, same Ma, but different
            Ga, Dha and Ni. Three small substitutions and you've moved between two
            entirely different emotional worlds.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["major", "naturalMinor", "harmonicMinor"]} />
      </Chapter>

      <Chapter eyebrow="Building scales by permutation" title="Twelve options for the first five notes alone">
        <Prose>
          <p>
            Here's the practical recipe. Take the first five notes of your scale —
            <strong> Sa, Re, Ga, Ma, Pa</strong>. Sa is fixed and Pa is fixed. You have
            six valid Re-Ga pairs (because of the pitch-overlap rule), and two Ma's to
            choose from. That gives you 6 × 2 = <strong>12 different five-note prefixes</strong>
            to start any seven-note scale.
          </p>
          <p>
            Then for the upper four notes — Pa, Dha, Ni, Sa — Pa and the octave are
            fixed, leaving six valid Dha-Ni pairs. So each of the 12 lower halves can
            pair with any of the 6 upper halves: 12 × 6 = 72 combinations. Every Western
            major and minor scale, every mode, harmonic minor, melodic minor, Phrygian
            dominant, Hungarian minor, and dozens of less-familiar Carnatic scales — all
            72 of them — emerge from the same simple recipe.
          </p>
        </Prose>

        <TheoryBox label="A daily practice">
          Pick a key — any key. Pick a Re-Ga pair. Pick a Ma. Play those five notes up
          and down. Then pick a Dha-Ni pair. Play all seven up and down. You've just
          built one Melakarta raga. Do this for ten minutes a day and within a few
          months you'll have your fingers (and ears) on most of the seventy-two.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Why this matters for any musician" title="Beyond the twelve canonical Western scales">
        <Prose>
          <p>
            You don't need to convert to Carnatic music to gain from this framework.
            The Melakarta system is, more than anything, an <strong>ear-training
            machine</strong>. Once you can hear the difference between Shuddha Ri and
            Chatushruti Ri, between Sadharana Ga and Antara Ga, between Shuddha Ma and Prati
            Ma — once those distinctions are real to you — every modal shift in jazz,
            every flamenco scale, every Eastern European folk melody becomes legible.
          </p>
          <p>
            And practically, for instrumentalists, the system trains the fingers to
            transpose. If you can play Sa R2 G3 M1 Pa D2 N3 (the major scale) in C, you
            can play it in any key by sliding the same Melakarta pattern up or down.
            Same with all 72. That's the kind of fluency that lets musicians improvise
            over chord changes they've never encountered — they're not memorising
            scales, they're recognising patterns.
          </p>
        </Prose>

        <Glossary>
          <Term name="Melakarta" def="The 72 parent scales of Carnatic music. Every other Indian raga is a derivative of one of these." />
          <Term name="Swara" def="A scale degree, with an associated syllable. Sa, Re, Ga, Ma, Pa, Dha, Ni — corresponding to 1, 2, 3, 4, 5, 6, 7." />
          <Term name="Shuddha" def="Carnatic for 'pure' — always the LOWEST variant of a degree. The higher variants have names of their own (Chatushruti, Shatshruti, Sadharana, Antara, Kaisiki, Kakali, Prati)." />
          <Term name="Sa and Pa" def="The two fixed notes — the root and the perfect 5th. They never change in any Melakarta raga." />
          <Term name="Pitch-overlap rule" def="Two swaras may share the same pitch (e.g. R2 = G1 = D), but they cannot both appear in the same scale. This constraint reduces 162 naïve permutations to 72 valid ones." />
          <Term name="Shankarabharanam" def="The Carnatic name for the major scale (Sa R2 G3 M1 Pa D2 N3)." />
          <Term name="Natabhairavi" def="The Carnatic name for the natural minor scale (Sa R2 G2 M1 Pa D1 N2)." />
        </Glossary>

        <MTQuote>
          It'll be a great way to train your ear to understand intervals very well, and also
          to practice an instrument well, to transpose and to change things very
          efficiently.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="What to practise next" title="From scale to song">
        <Prose>
          <p>
            Three exercises will move this from theory into your hands. <strong>One:</strong>
            spell out five Western scales you already know — major, natural minor,
            harmonic minor, Dorian, Phrygian — using Melakarta notation. Notice which
            Re, Ga, Ma, Dha and Ni each one uses. <strong>Two:</strong> pick three
            Melakarta combinations you've never played and listen to them slowly,
            note by note, on the piano. Try to write a four-bar melody in each.
            <strong> Three:</strong> transpose one chosen raga to three different keys —
            C, F♯, A♭. The same pattern of swaras should sound recognisable across all
            three roots, even though the actual pitches differ.
          </p>
          <p>
            Music theory, at its best, is a way of seeing patterns in sound. The
            Melakarta framework offers one of the most complete pattern-libraries any
            musical culture has ever built. Borrow what you need.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/16a-melakarta-swaras-key-of-C-72-ragas.png"
          caption="Jason's note: the Carnatic Melakarta system overlaid on Western intervals. r₁/r₂/r₃, g₁/g₂/g₃, m₁/m₂, p, d₁/d₂/d₃, n₁/n₂/n₃ — every swara position cross-referenced with its Western equivalent (m2, M2, m3, M3, P4, ♯4, P5, m6, M6, m7, M7). This is how the 72 Melakarta ragas systematically cover every possible heptatonic scale."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-17"
        label="Chapter 17 · Salsa Rhythm Patterns - Tresillo (Slow & Fast)"
        why="Scales handled, pitches mapped — the next move is rhythm. Tresillo is the three-hit Cuban pattern that has quietly become the most important groove in modern popular music."
      />
    </LessonShell>
  );
}
